import { useCallback, useEffect, useRef, type PropsWithChildren } from "react";
import { useBlocker, useLocation, useNavigate } from "react-router-dom";
import {
  GsbNavigationProvider,
  type NavigationAdapter,
} from "@gsb-core/app-kit";
import { withLanguagePrefix } from "./language-path";

export function WorkspaceNavigationBoundary({ children }: PropsWithChildren) {
  const location = useLocation();
  const routerNavigate = useNavigate();
  const policies = useRef(new Set<() => Promise<boolean>>());
  const block = useCallback((confirmLeave: () => Promise<boolean>) => {
    policies.current.add(confirmLeave);
    return () => {
      policies.current.delete(confirmLeave);
    };
  }, []);
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      policies.current.size > 0 &&
      (currentLocation.pathname !== nextLocation.pathname ||
        currentLocation.search !== nextLocation.search ||
        currentLocation.hash !== nextLocation.hash),
  );
  const latestBlocker = useRef(blocker);
  latestBlocker.current = blocker;
  const confirming = useRef(false);
  useEffect(() => {
    if (blocker.state !== "blocked" || confirming.current) return;
    confirming.current = true;
    const decide = async () => {
      let leave = true;
      for (const policy of policies.current) {
        if (!(await policy())) {
          leave = false;
          break;
        }
      }
      const current = latestBlocker.current;
      if (current.state === "blocked") {
        if (leave) current.proceed();
        else current.reset();
      }
      confirming.current = false;
    };
    void decide();
  }, [blocker]);
  const navigation: NavigationAdapter = {
    block,
    href: (path) => withLanguagePrefix(path, location.pathname),
    navigate: (path, options) => {
      void routerNavigate(path, options);
    },
    go: (delta) => {
      void routerNavigate(delta);
    },
  };

  return (
    <GsbNavigationProvider navigation={navigation} location={location}>
      {children}
    </GsbNavigationProvider>
  );
}
